/**
 * Step 1: pick a username. Calls `/username/check` (debounced) while typing
 * and `/username/claim` on `Continue`. The claim happens on `Continue`, not
 * on a keystroke, because the onboarding feature doc's business rule 1
 * says two people typing the same name at once must not both get it.
 */
import {
  useUsernameCheck,
  useUsernameClaim,
} from "@sportsmet/profile-sdk/private";
import { Button, Input } from "@sportsmet/ui";
import { type ReactNode, useEffect, useRef, useState } from "react";

import { showApiError } from "../../errors/apiErrorBus";
import {
  normalizeUsername,
  USERNAME_FORMAT_ERROR,
  USERNAME_PATTERN,
} from "../editProfile/editProfileLogic";
import {
  CONTINUE_LABEL,
  CONTINUE_PENDING_LABEL,
  USERNAME_CHECKING_LABEL,
  USERNAME_CLAIMED_HINT,
  USERNAME_HEADLINE,
  USERNAME_LABEL,
  USERNAME_PLACEHOLDER,
  USERNAME_SUB,
  usernameFreeMessage,
  usernameTakenMessage,
} from "./onboardingCopy";
import { OnboardingStepFrame } from "./OnboardingStepFrame";

export const USERNAME_CHECK_DEBOUNCE_MS = 400;

export type typeUsernameAvailability =
  "idle" | "checking" | "available" | "taken" | "invalidFormat";

export const UsernameStep = ({
  stepIndex,
  stepTotal,
  claimedUsername,
  onClaimed,
}: {
  /** This fan's position and total, per Q274. Passed straight to OnboardingStepFrame. */
  stepIndex: number;
  stepTotal: number;
  /**
   * The profile's already-claimed username, or null when the fan has not
   * claimed one yet. A profile row only exists after the first successful
   * claim, so a non-null value means this fan is revisiting the step.
   */
  claimedUsername: string | null;
  /**
   * Called after `/username/claim` succeeds. The route advances the step
   * and raises its ceiling from the `reachedServerStep` value passed here,
   * which came straight off the claim response.
   */
  onClaimed: (input: { reachedServerStep: string | null }) => void;
}): ReactNode => {
  const isLocked = claimedUsername !== null;
  const check = useUsernameCheck();
  const claim = useUsernameClaim();
  const [username, setUsername] = useState(claimedUsername ?? "");
  const [availability, setAvailability] =
    useState<typeUsernameAvailability>("idle");
  const isClaiming = useRef(false);

  useEffect(() => {
    if (isLocked) return;
    if (username === "") {
      setAvailability("idle");
      return;
    }
    if (!USERNAME_PATTERN.test(username)) {
      setAvailability("invalidFormat");
      return;
    }
    setAvailability("checking");
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const result = await check.mutateAsync({ username });
          const data = result.data;
          if (data?.isAvailable === true) {
            setAvailability("available");
          } else if (data?.unavailableReason === "taken") {
            setAvailability("taken");
          } else if (data?.unavailableReason === "invalidFormat") {
            setAvailability("invalidFormat");
          } else {
            setAvailability("idle");
          }
        } catch {
          setAvailability("idle");
        }
      })();
    }, USERNAME_CHECK_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [username]);

  async function handleContinue(): Promise<void> {
    if (isLocked) {
      onClaimed({ reachedServerStep: null });
      return;
    }
    if (isClaiming.current) return;
    isClaiming.current = true;

    let result;
    try {
      result = await claim.mutateAsync({ username });
    } catch {
      showApiError({ error: { code: "INTERNAL_CALL_FAILED", message: "" } });
      isClaiming.current = false;
      return;
    }

    if (showApiError(result)) {
      isClaiming.current = false;
      return;
    }

    onClaimed({
      reachedServerStep: result.data?.profile.onboardingStep ?? "details",
    });
  }

  return (
    <OnboardingStepFrame
      stepIndex={stepIndex}
      stepTotal={stepTotal}
      headline={USERNAME_HEADLINE}
      sub={USERNAME_SUB}
    >
      <Input
        label={USERNAME_LABEL}
        required
        placeholder={USERNAME_PLACEHOLDER}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        autoComplete="username"
        inputMode="text"
        data-testid="onboarding-username"
        prefix="@"
        disabled={isLocked || claim.isPending}
        value={username}
        error={
          isLocked
            ? undefined
            : availability === "taken"
              ? usernameTakenMessage(username)
              : availability === "invalidFormat"
                ? USERNAME_FORMAT_ERROR
                : undefined
        }
        hint={
          isLocked
            ? USERNAME_CLAIMED_HINT
            : availability === "available"
              ? usernameFreeMessage(username)
              : undefined
        }
        onChange={(event) => {
          setUsername(normalizeUsername(event.target.value));
        }}
      />
      {!isLocked && availability === "checking" ? (
        <p
          role="status"
          className="text-caption text-ink-secondary"
          data-testid="onboarding-username-checking"
        >
          {USERNAME_CHECKING_LABEL}
        </p>
      ) : null}
      <Button
        variant="primary"
        fullWidth
        loading={claim.isPending}
        loadingLabel={CONTINUE_PENDING_LABEL}
        disabled={isLocked ? false : availability !== "available"}
        data-testid="onboarding-username-continue"
        onClick={() => {
          void handleContinue();
        }}
      >
        {CONTINUE_LABEL}
      </Button>
    </OnboardingStepFrame>
  );
};
